import React, { useState, useRef, useEffect } from 'react';
import { apiFetch, c } from '../api';

const API = '/api';

// ── Design tokens ─────────────────────────────────────────────────────────────

const T = {
  bg:         '#f4ecd9',
  paper:      '#fdf6e3',
  paperHigh:  '#fffaf0',
  ink:        '#22180e',
  inkSoft:    '#6a5a3e',
  inkMute:    '#a89570',
  accent:     '#c75a2c',
  accentSoft: '#f8dcc1',
  teal:       '#4e8a90',
  tealSoft:   '#cce0e2',
  divider:    'rgba(34,24,14,0.08)',
  display:    "'Newsreader', Georgia, serif",
  body:       "'Work Sans', system-ui, sans-serif",
  radius:     18,
  radiusLg:   28,
};

// ── Icons ─────────────────────────────────────────────────────────────────────

function Icon({ name, size = 24, stroke = 1.5, color = 'currentColor' }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'mic':      return <svg {...p}><rect x="9" y="3" width="6" height="12" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" /><path d="M12 18v3" /></svg>;
    case 'pen':      return <svg {...p}><path d="M14 4.5l5.5 5.5L8 21.5H2.5V16L14 4.5z" /><path d="M13 5.5l5.5 5.5" /></svg>;
    case 'sparkle':  return <svg {...p}><path d="M12 3v6M12 15v6M3 12h6M15 12h6" /><path d="M6.5 6.5l3 3M14.5 14.5l3 3M17.5 6.5l-3 3M9.5 14.5l-3 3" opacity="0.6" /></svg>;
    case 'book':     return <svg {...p}><path d="M4 4.5C4 3.7 4.7 3 5.5 3H11v17H5.5C4.7 20 4 19.3 4 18.5v-14z" /><path d="M20 4.5C20 3.7 19.3 3 18.5 3H13v17h5.5c.8 0 1.5-.7 1.5-1.5v-14z" /></svg>;
    case 'arrowLeft':return <svg {...p}><path d="M19 12H5M11 6l-6 6 6 6" /></svg>;
    case 'arrowRight':return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'check':    return <svg {...p}><path d="M4 12.5l5 5L20 6.5" /></svg>;
    case 'more':     return <svg {...p}><circle cx="5" cy="12" r="1.2" fill={color} stroke="none" /><circle cx="12" cy="12" r="1.2" fill={color} stroke="none" /><circle cx="19" cy="12" r="1.2" fill={color} stroke="none" /></svg>;
    case 'play':     return <svg {...p}><path d="M6 4.5v15l13-7.5L6 4.5z" fill={color} stroke="none" /></svg>;
    case 'pause':    return <svg {...p}><rect x="6" y="4.5" width="4" height="15" rx="1" fill={color} stroke="none" /><rect x="14" y="4.5" width="4" height="15" rx="1" fill={color} stroke="none" /></svg>;
    case 'shield':   return <svg {...p}><path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z" /><path d="M9 12l2 2 4-4" /></svg>;
    default: return null;
  }
}

// ── Waveform ──────────────────────────────────────────────────────────────────

function Waveform({ color = T.accent, count = 28, height = 64 }) {
  const heights = [0.25,0.4,0.55,0.35,0.65,0.8,0.5,0.7,0.95,0.6,0.85,0.75,1.0,0.65,0.85,0.55,0.95,0.7,0.55,0.8,0.65,0.45,0.7,0.55,0.85,0.5,0.65,0.4];
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height, width: '100%' }}>
      {Array.from({ length: count }).map((_, i) => {
        const peak = heights[i % heights.length];
        const dur = 0.7 + (i % 7) * 0.12;
        const delay = (i * 0.06) % 1.4;
        return (
          <div key={i} style={{
            width: 4, height: `${Math.max(20, peak * 100)}%`,
            background: color, borderRadius: 2,
            transformOrigin: 'center',
            animation: `wfPulse ${dur}s ease-in-out ${delay}s infinite alternate`,
          }} />
        );
      })}
    </div>
  );
}

// ── Connection warning ────────────────────────────────────────────────────────

function useConnectionQuality() {
  const [quality, setQuality] = useState('good');
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!navigator.onLine) { if (!cancelled) setQuality('offline'); return; }
      try {
        const start = Date.now();
        await fetch('/api/health', { method: 'GET', cache: 'no-store', signal: AbortSignal.timeout(3000) });
        const latency = Date.now() - start;
        if (!cancelled) setQuality(latency < 800 ? 'good' : latency < 2500 ? 'fair' : 'poor');
      } catch { if (!cancelled) setQuality('poor'); }
    };
    check();
    const interval = setInterval(check, 8000);
    window.addEventListener('offline', () => !cancelled && setQuality('offline'));
    window.addEventListener('online', check);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  return quality;
}

function ConnectionWarning({ quality }) {
  if (quality === 'good') return null;
  const cfg = {
    fair:    { msg: 'Your connection is slow — recording will still work, but it may take a moment.', bg: '#fdf3e4', border: T.accentSoft, color: T.accent },
    poor:    { msg: 'Weak connection. Try moving closer to your router before recording.', bg: '#fdf3e4', border: T.accentSoft, color: T.accent },
    offline: { msg: "You're offline. Please reconnect — we need the internet to save your story.", bg: '#fdf0ee', border: '#f0c4b8', color: '#b04020' },
  }[quality];
  return (
    <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: T.radius, padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%' }}>
      <Icon name="shield" size={16} stroke={1.5} color={cfg.color} />
      <span style={{ fontFamily: T.body, fontSize: 14, color: cfg.color, lineHeight: 1.5, flex: 1 }}>{cfg.msg}</span>
    </div>
  );
}

// ── Wordmark ──────────────────────────────────────────────────────────────────

function Wordmark() {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
      <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 400, fontStyle: 'italic', color: T.ink, letterSpacing: -0.3 }}>memories</span>
      <span style={{ fontFamily: T.display, fontSize: 22, color: T.accent }}>.</span>
    </div>
  );
}

// ── NEW badge ─────────────────────────────────────────────────────────────────

function NewBadge() {
  return (
    <span style={{ background: T.accent, color: T.paper, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase', flexShrink: 0, fontFamily: T.body }}>
      New
    </span>
  );
}

// ── Tag chip ──────────────────────────────────────────────────────────────────

function TagChip({ label, type = 'person' }) {
  const colors = {
    person: { bg: T.accentSoft, color: T.accent },
    place:  { bg: T.tealSoft,   color: T.teal   },
    topic:  { bg: T.divider,    color: T.inkSoft },
  };
  const { bg, color } = colors[type] || colors.topic;
  return (
    <span style={{ background: bg, color, fontFamily: T.body, fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 99 }}>
      {label}
    </span>
  );
}

// ── Global styles ─────────────────────────────────────────────────────────────

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300..700;1,6..72,300..700&family=Work+Sans:wght@300..700&display=swap');
  @keyframes wfPulse { from { transform: scaleY(0.15); } to { transform: scaleY(1); } }
  @keyframes recPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.18); opacity: 0.75; } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  select option { background: #fdf6e3; color: #22180e; }
  select { color-scheme: light; }
  * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  body { margin: 0; background: ${T.bg}; color: ${T.ink}; font-family: ${T.body}; -webkit-font-smoothing: antialiased; }
  button { font-family: inherit; cursor: pointer; }
  input, textarea { font-family: inherit; }
  .mem-input:focus { outline: none; border-color: ${T.accent} !important; box-shadow: 0 0 0 3px ${T.accentSoft} !important; }
  .mem-btn-primary:active { opacity: 0.88; transform: scale(0.98); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// SCREENS
// ─────────────────────────────────────────────────────────────────────────────

// ── Home ──────────────────────────────────────────────────────────────────────

function HomeScreen({ user, onRecord, onWrite, onPrompt, onArchive, onLogout, loadingPrompt, connectionQuality }) {
  const firstName = user?.nickname || user?.name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const timeLabel = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Nav */}
      <div style={{ padding: '14px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Wordmark />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={onArchive} style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="book" size={18} stroke={1.5} color={T.inkMute} />
            Stories
          </button>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 14 }}>
            Sign out
          </button>
        </div>
      </div>

      <div style={{ flex: 1, padding: '8px 22px 40px', display: 'flex', flexDirection: 'column' }}>
        {/* Greeting */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 8px' }}>
            {timeLabel}
          </p>
          <h1 style={{ fontFamily: T.display, fontSize: 40, fontWeight: 400, letterSpacing: -0.5, lineHeight: 1.08, color: T.ink, margin: 0 }}>
            Hello, <em style={{ color: T.accent }}>{firstName}.</em>
          </h1>
        </div>

        <ConnectionWarning quality={connectionQuality} />

        <p style={{ fontFamily: T.body, fontSize: 13, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 14px' }}>
          What would you like to do?
        </p>

        {/* Choice cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { icon: 'mic',     label: 'Tell a story aloud',    sub: "I'll listen and remember it.",  color: T.accent, tint: T.accentSoft, action: onRecord  },
            { icon: 'pen',     label: 'Write a memory down',   sub: 'Type it out instead.',           color: T.teal,   tint: T.tealSoft,   action: onWrite   },
            { icon: 'sparkle', label: 'Help me — give me a question', sub: 'Get a prompt to answer.',color: T.inkSoft, tint: T.divider,    action: onPrompt, loading: loadingPrompt },
          ].map(ch => (
            <button key={ch.label} onClick={ch.action} disabled={ch.loading} style={{
              display: 'flex', alignItems: 'center', gap: 18,
              padding: '18px 20px',
              background: T.paperHigh,
              border: `1px solid ${T.divider}`,
              borderRadius: T.radius,
              textAlign: 'left', width: '100%',
              minHeight: 80,
              transition: 'transform 0.12s, box-shadow 0.12s',
              boxShadow: '0 1px 0 rgba(34,24,14,0.03)',
            }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: ch.tint, color: ch.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name={ch.icon} size={24} stroke={1.5} color={ch.color} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: T.display, fontSize: 20, color: T.ink, letterSpacing: -0.2, lineHeight: 1.2, marginBottom: 3 }}>
                  {ch.loading ? 'Finding a question…' : ch.label}
                </div>
                <div style={{ fontFamily: T.body, fontSize: 14, color: T.inkSoft }}>{ch.sub}</div>
              </div>
              <Icon name="arrowRight" size={18} stroke={1.5} color={T.inkMute} />
            </button>
          ))}
        </div>

        {/* Archive link */}
        <button onClick={onArchive} style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 15, padding: '20px 0 0', display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'center' }}>
          Read your stories
          <Icon name="arrowRight" size={16} stroke={1.5} color={T.inkMute} />
        </button>
      </div>
    </div>
  );
}

// ── Prompted ──────────────────────────────────────────────────────────────────

function PromptedScreen({ prompt, onRecord, onWrite, onBack }) {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 22px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.inkSoft, display: 'flex', alignItems: 'center', gap: 6, fontFamily: T.body, fontSize: 15 }}>
          <Icon name="arrowLeft" size={18} stroke={1.5} color={T.inkSoft} />
          Back
        </button>
      </div>

      <div style={{ flex: 1, padding: '8px 22px 40px', display: 'flex', flexDirection: 'column' }}>
        <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 14px' }}>
          Here's something to think about
        </p>

        {/* Prompt card */}
        <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '24px 22px', marginBottom: 28, boxShadow: '0 1px 0 rgba(34,24,14,0.03), 0 16px 32px -12px rgba(120,80,40,0.10)' }}>
          <p style={{ fontFamily: T.display, fontSize: 24, fontWeight: 400, lineHeight: 1.4, color: T.ink, margin: 0, letterSpacing: -0.2 }}>
            {prompt}
          </p>
        </div>

        <p style={{ fontFamily: T.body, fontSize: 13, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 12px' }}>
          How would you like to answer?
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={onRecord} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', background: T.ink, border: 'none', borderRadius: T.radiusLg, textAlign: 'left', width: '100%' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="mic" size={22} stroke={1.5} color={T.paper} />
            </div>
            <div>
              <div style={{ fontFamily: T.display, fontSize: 20, color: T.paper, letterSpacing: -0.2 }}>Record my answer</div>
              <div style={{ fontFamily: T.body, fontSize: 14, color: 'rgba(253,246,227,0.65)', marginTop: 2 }}>Speak and I'll remember it</div>
            </div>
          </button>

          <button onClick={() => onWrite(prompt)} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radiusLg, textAlign: 'left', width: '100%' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: T.tealSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="pen" size={22} stroke={1.5} color={T.teal} />
            </div>
            <div>
              <div style={{ fontFamily: T.display, fontSize: 20, color: T.ink, letterSpacing: -0.2 }}>Write my answer</div>
              <div style={{ fontFamily: T.body, fontSize: 14, color: T.inkSoft, marginTop: 2 }}>Type it out instead</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Recording ─────────────────────────────────────────────────────────────────

function RecordingScreen({ prompt, seconds, onStop, onCancel }) {
  const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 22px' }}>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: T.inkSoft, fontFamily: T.body, fontSize: 15 }}>Cancel</button>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 24px 60px', gap: 24 }}>
        {/* Privacy pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.tealSoft, borderRadius: 999, padding: '8px 14px' }}>
          <Icon name="shield" size={14} stroke={1.5} color={T.teal} />
          <span style={{ fontFamily: T.body, fontSize: 13, color: T.teal, fontWeight: 500 }}>Only your family will hear this.</span>
        </div>

        {prompt && (
          <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '16px 18px', width: '100%', maxWidth: 340 }}>
            <p style={{ fontFamily: T.display, fontSize: 17, color: T.inkSoft, margin: 0, lineHeight: 1.45, fontStyle: 'italic' }}>{prompt}</p>
          </div>
        )}

        {!prompt && <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft }}>Speak clearly — take your time.</p>}

        {/* Waveform */}
        <div style={{ width: '100%', maxWidth: 340 }}>
          <Waveform color={T.accent} count={28} height={72} />
        </div>

        {/* Recording dot + timer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#d9534f', display: 'inline-block', animation: 'recPulse 1.4s ease-in-out infinite' }} />
          <span style={{ fontFamily: T.body, fontSize: 40, fontWeight: 300, color: T.ink, letterSpacing: -1, fontVariantNumeric: 'tabular-nums' }}>{fmt(seconds)}</span>
        </div>

        <button onClick={onStop} style={{
          background: T.ink, border: 'none', color: T.paper,
          padding: '16px 44px', borderRadius: T.radiusLg,
          fontFamily: T.body, fontSize: 17, fontWeight: 600,
          minWidth: 200, minHeight: 56,
        }}>
          I'm finished
        </button>
      </div>
    </div>
  );
}

// ── Write ─────────────────────────────────────────────────────────────────────

function WriteScreen({ writingPrompt, writingText, setWritingText, onSubmit, onBack }) {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.inkSoft, fontFamily: T.body, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="arrowLeft" size={18} stroke={1.5} color={T.inkSoft} />
          Back
        </button>
        <button onClick={onSubmit} disabled={!writingText.trim()} style={{
          background: writingText.trim() ? T.ink : 'transparent',
          border: writingText.trim() ? 'none' : `1px solid ${T.divider}`,
          color: writingText.trim() ? T.paper : T.inkMute,
          fontFamily: T.body, fontSize: 15, fontWeight: 600,
          padding: '8px 18px', borderRadius: 999,
          transition: 'all 0.15s',
        }}>
          Save memory
        </button>
      </div>

      <div style={{ flex: 1, padding: '8px 22px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {writingPrompt && (
          <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 18px' }}>
            <p style={{ fontFamily: T.display, fontSize: 17, color: T.inkSoft, margin: 0, lineHeight: 1.45, fontStyle: 'italic' }}>{writingPrompt}</p>
          </div>
        )}

        <textarea
          className="mem-input"
          value={writingText}
          onChange={e => setWritingText(e.target.value)}
          placeholder={writingPrompt ? 'Write your answer here…' : 'Write your memory here… take your time, there\'s no rush.'}
          autoFocus
          style={{
            flex: 1, minHeight: 280,
            background: 'transparent', border: 'none',
            fontFamily: T.display, fontSize: 22, color: T.ink,
            lineHeight: 1.6, resize: 'none', padding: 0, outline: 'none',
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute }}>
            {writingText.trim() ? `${writingText.trim().split(/\s+/).length} words` : 'A blank page, waiting for you.'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="shield" size={13} stroke={1.5} color={T.teal} />
            <span style={{ fontFamily: T.body, fontSize: 12, color: T.teal }}>Only your family can read this.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Processing ────────────────────────────────────────────────────────────────

function ProcessingScreen() {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 20 }}>
      <div style={{ width: 44, height: 44, border: `2px solid ${T.divider}`, borderTop: `2px solid ${T.accent}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <h2 style={{ fontFamily: T.display, fontSize: 28, fontWeight: 400, color: T.ink, margin: 0, textAlign: 'center', letterSpacing: -0.3 }}>
        We're <em style={{ color: T.accent }}>typing it up.</em>
      </h2>
      <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft, margin: 0, textAlign: 'center', lineHeight: 1.6 }}>
        Your story is safe. This usually takes about 30 seconds.
      </p>
    </div>
  );
}

// ── Saved / Done ──────────────────────────────────────────────────────────────

function SavedScreen({ onRecordAnother, onViewStories }) {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 20 }}>
      {/* Success medallion */}
      <div style={{ width: 88, height: 88, borderRadius: '50%', background: T.tealSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px -6px rgba(199,90,44,0.40)' }}>
          <Icon name="check" size={30} stroke={2} color={T.paper} />
        </div>
      </div>

      <div style={{ textAlign: 'center' }}>
        <p style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 10px' }}>Saved</p>
        <h2 style={{ fontFamily: T.display, fontSize: 32, fontWeight: 400, letterSpacing: -0.4, color: T.ink, margin: '0 0 10px' }}>
          Your story is <em style={{ color: T.accent }}>safe.</em>
        </h2>
        <p style={{ fontFamily: T.body, fontSize: 16, color: T.inkSoft, lineHeight: 1.6, margin: 0 }}>
          Your family will be able to hear it shortly.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 320 }}>
        <button onClick={onRecordAnother} style={{ background: T.ink, border: 'none', color: T.paper, padding: '16px', borderRadius: T.radiusLg, fontFamily: T.body, fontSize: 16, fontWeight: 600, minHeight: 56 }}>
          Tell another story
        </button>
        <button onClick={onViewStories} style={{ background: 'none', border: `1px solid ${T.divider}`, color: T.inkSoft, padding: '14px', borderRadius: T.radiusLg, fontFamily: T.body, fontSize: 16 }}>
          See my stories
        </button>
      </div>
    </div>
  );
}

// ── Error ─────────────────────────────────────────────────────────────────────

function ErrorScreen({ msg, onBack }) {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 16, textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fdf0ee', border: `1px solid #f0c4b8`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.display, fontSize: 28, color: T.accent }}>!</div>
      <h2 style={{ fontFamily: T.display, fontSize: 28, fontWeight: 400, color: T.ink, margin: 0, letterSpacing: -0.3 }}>Something went <em style={{ color: T.accent }}>wrong.</em></h2>
      <p style={{ fontFamily: T.body, fontSize: 15, color: T.inkSoft, margin: 0, lineHeight: 1.6 }}>{msg}</p>
      <button onClick={onBack} style={{ background: T.ink, border: 'none', color: T.paper, padding: '14px 32px', borderRadius: T.radiusLg, fontFamily: T.body, fontSize: 16, fontWeight: 600, marginTop: 8 }}>Try again</button>
    </div>
  );
}

// ── Archive ───────────────────────────────────────────────────────────────────

// ── Past Weaves ───────────────────────────────────────────────────────────────

function PastWeaves({ userId, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [weaves, setWeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/memories/weaves?user_id=${userId}`);
      setWeaves(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [refreshKey]);

  const toggle = () => setOpen(o => !o);

  return (
    <div style={{ marginBottom: 14 }}>
      <button onClick={toggle} style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: 999, color: T.teal, fontFamily: T.body, fontSize: 14, fontWeight: 600, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, width: '100%', justifyContent: 'center' }}>
        <Icon name="book" size={17} stroke={1.5} color={T.teal} />
        {open ? 'Hide my saved stories' : `My saved stories${weaves.length > 0 ? ` (${weaves.length})` : ''}`}
        <span style={{ fontSize: 11, color: T.inkMute }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {loading && <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute }}>Loading…</div>}
          {!loading && weaves.length === 0 && (
            <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, fontStyle: 'italic' }}>No saved stories yet. Weave one above and it will be kept here.</div>
          )}
          {weaves.map(w => (
            <div key={w.id} style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '12px 14px', marginBottom: 8 }}>
              <button onClick={() => setExpandedId(expandedId === w.id ? null : w.id)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ fontFamily: T.display, fontSize: 16, color: T.ink, letterSpacing: -0.1 }}>
                    {w.query ? `About "${w.query}"` : 'My whole life story'}
                  </span>
                  <span style={{ fontFamily: T.body, fontSize: 12, color: T.inkMute, flexShrink: 0 }}>
                    {new Date(w.created_at + 'Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div style={{ fontFamily: T.body, fontSize: 12, color: T.inkMute, marginTop: 3 }}>
                  {w.memory_count} {w.memory_count === 1 ? 'memory' : 'memories'}
                  {w.requested_by_name ? ` · woven by ${w.requested_by_name.split(' ')[0]}` : ''}
                </div>
              </button>

              {expandedId === w.id && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${T.divider}`, paddingTop: 12 }}>
                  <div style={{ fontFamily: T.display, fontSize: 17, color: T.ink, lineHeight: 1.7 }}>
                    {w.narrative.split('\n').filter(Boolean).map((para, i) => <p key={i} style={{ margin: '0 0 12px 0' }}>{para}</p>)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArchiveScreen({ memories, onSelect, onBack, onWeave, weaving, weaveResult, setWeaveResult, weaveSearch, setWeaveSearch, userId, weaveRefreshKey }) {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Nav */}
      <div style={{ padding: '14px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${T.divider}` }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.inkSoft, display: 'flex', alignItems: 'center', gap: 6, fontFamily: T.body, fontSize: 15 }}>
          <Icon name="arrowLeft" size={18} stroke={1.5} color={T.inkSoft} />
          Home
        </button>
        <Wordmark />
        <div style={{ width: 60 }} />
      </div>

      <div style={{ padding: '16px 20px 0' }}>
        {/* Weave bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: 999, padding: '10px 14px' }}>
            <Icon name="sparkle" size={16} stroke={1.5} color={T.inkMute} />
            <input
              className="mem-input"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontFamily: T.body, fontSize: 15, color: T.ink }}
              placeholder="Weave about a person or topic…"
              value={weaveSearch}
              onChange={e => setWeaveSearch(e.target.value)}
            />
          </div>
          <button onClick={onWeave} disabled={weaving} style={{ background: T.accent, border: 'none', color: T.paper, padding: '10px 16px', borderRadius: 999, fontFamily: T.body, fontSize: 14, fontWeight: 600, flexShrink: 0, minHeight: 44 }}>
            {weaving ? '…' : 'Weave'}
          </button>
        </div>

        {/* Weave result */}
        {weaving && (
          <div style={{ textAlign: 'center', padding: '16px 0', color: T.inkSoft, fontFamily: T.body, fontSize: 15, fontStyle: 'italic' }}>Weaving your story…</div>
        )}
        {weaveResult && (
          <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '18px 20px', marginBottom: 14 }}>
            <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.accent, margin: '0 0 12px' }}>✦ Your story</p>
            <div style={{ fontFamily: T.display, fontSize: 17, color: T.ink, lineHeight: 1.7 }}>
              {weaveResult.split('\n').filter(Boolean).map((para, i) => <p key={i} style={{ margin: '0 0 12px 0' }}>{para}</p>)}
            </div>
            <button onClick={() => setWeaveResult(null)} style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 13, padding: 0 }}>Dismiss</button>
          </div>
        )}
        <PastWeaves userId={userId} refreshKey={weaveRefreshKey} />
      </div>

      {/* Memory list */}
      <div style={{ flex: 1, padding: '4px 20px 40px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {memories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ fontFamily: T.display, fontSize: 26, fontWeight: 400, color: T.inkSoft, margin: '0 0 8px', fontStyle: 'italic' }}>A blank page, waiting for you.</p>
            <p style={{ fontFamily: T.body, fontSize: 15, color: T.inkMute }}>Your recordings will appear here.</p>
          </div>
        ) : memories.map(m => (
          <button key={m.id} onClick={() => onSelect(m)} style={{
            background: T.paperHigh, border: `1px solid ${T.divider}`,
            borderRadius: T.radius, padding: '16px 18px',
            textAlign: 'left', width: '100%',
            boxShadow: '0 1px 0 rgba(34,24,14,0.02)',
            transition: 'transform 0.12s, box-shadow 0.12s',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.accent }}>{m.decade}</span>
                {m.is_new && <NewBadge />}
                {m.rating === 'R' && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.accent, border: `1px solid ${T.accent}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>R</span>}
                {m.rating === 'P' && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.inkMute, border: `1px solid ${T.inkMute}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>PRIVATE</span>}
                {m.is_prompted === 1 && m.suggested_prompt_id && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.teal, border: `1px solid ${T.teal}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>✦ Prompted</span>}
                {m.is_prompted === 1 && !m.suggested_prompt_id && <span style={{ fontFamily: T.body, fontSize: 10, fontWeight: 700, color: T.inkMute, border: `1px solid ${T.inkMute}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.05em' }}>AI Prompted</span>}
              </div>
              <span style={{ fontFamily: T.body, fontSize: 12, color: T.inkMute }}>
                {new Date(m.recorded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div style={{ fontFamily: T.display, fontSize: 19, color: T.ink, letterSpacing: -0.2, lineHeight: 1.2, marginBottom: m.summary ? 6 : 0 }}>{m.title}</div>
            {m.location_name && <div style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute, marginBottom: 6 }}>📍 {m.location_name}</div>}
            {m.summary && <div style={{ fontFamily: T.body, fontSize: 14, color: T.inkSoft, lineHeight: 1.55 }}>{m.summary}</div>}
            {/* Tag chips */}
            {(m.people?.length > 0 || m.topics?.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                {m.people?.slice(0, 3).map(p => <TagChip key={p} label={p} type="person" />)}
                {m.topics?.slice(0, 2).map(t => <TagChip key={t} label={t} type="topic" />)}
            {m.is_new && (
              <div onClick={e => { e.stopPropagation(); apiFetch(`/memories/${m.id}/review`, { method: 'POST' }).then(() => setMemories(prev => prev.map(x => x.id === m.id ? { ...x, is_new: false } : x))).catch(() => {}); }} style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: `1px solid ${T.divider}`, borderRadius: 99, padding: '4px 12px', cursor: 'pointer' }}>
                <span style={{ fontFamily: T.body, fontSize: 12, color: T.inkMute }}>✓ Mark as read</span>
              </div>
            )}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Memory detail ─────────────────────────────────────────────────────────────

// ── Memory Photos ─────────────────────────────────────────────────────────────

function MemoryPhotos({ memoryId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [urls, setUrls] = useState({});
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/photos/memory/${memoryId}`);
      setPhotos(data);
      const token = localStorage.getItem('memories_token');
      const next = {};
      await Promise.all(data.map(async p => {
        try {
          const res = await fetch(p.url, { headers: { 'Authorization': `Bearer ${token}` } });
          if (res.ok) next[p.id] = URL.createObjectURL(await res.blob());
        } catch {}
      }));
      setUrls(next);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [memoryId]);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('memory_id', memoryId);
      const token = localStorage.getItem('memories_token');
      const res = await fetch('/api/photos/upload', {
        method: 'POST',
        body: fd,
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Upload failed');
      }
      await load();
    } catch (err) { alert('Could not add photo: ' + err.message); }
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = async (id) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await apiFetch(`/photos/${id}`, { method: 'DELETE' });
      setPhotos(prev => prev.filter(p => p.id !== id));
    } catch (err) { alert('Could not remove: ' + err.message); }
  };

  return (
    <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: 0 }}>Photos</p>
        <button onClick={pick} disabled={uploading} style={{ background: 'none', border: 'none', color: T.accent, fontFamily: T.body, fontSize: 14, fontWeight: 600, padding: 0 }}>
          {uploading ? 'Adding…' : '+ Add photo'}
        </button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />

      {loading ? (
        <p style={{ fontFamily: T.body, fontSize: 14, color: T.inkMute, margin: 0 }}>Loading…</p>
      ) : photos.length === 0 ? (
        <p style={{ fontFamily: T.body, fontSize: 14, color: T.inkMute, margin: 0, lineHeight: 1.5 }}>
          No photos yet. Adding one helps bring this story to life.
        </p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {photos.map(p => (
            <div key={p.id} style={{ position: 'relative' }}>
              <button onClick={() => urls[p.id] && setViewing(urls[p.id])} style={{ background: 'none', border: 'none', padding: 0 }}>
                {urls[p.id] ? (
                  <img src={urls[p.id]} alt={p.caption || 'Memory photo'} style={{ width: 104, height: 104, objectFit: 'cover', borderRadius: 12, border: `1px solid ${T.divider}`, display: 'block' }} />
                ) : (
                  <div style={{ width: 104, height: 104, borderRadius: 12, background: T.paper, border: `1px solid ${T.divider}` }} />
                )}
              </button>
              <button onClick={() => removePhoto(p.id)} style={{ position: 'absolute', top: -6, right: -6, width: 24, height: 24, borderRadius: '50%', background: T.paper, border: `1px solid ${T.divider}`, color: T.inkMute, fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div onClick={() => setViewing(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(34,24,14,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <img src={viewing} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12 }} />
        </div>
      )}
    </div>
  );
}

function MemoryDetail({ memory: initialMemory, onBack, onDelete, onUpdate }) {
  const [m, setM] = useState(initialMemory);
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [rating, setRating] = useState(initialMemory.rating || null);
  const [visibility, setVisibility] = useState(initialMemory.visibility || 'private');
  const [savingControls, setSavingControls] = useState(false);
  const [transcriptDraft, setTranscriptDraft] = useState(m.transcript || '');
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [memoryIsNew, setMemoryIsNew] = useState(m.is_new);
  const audioRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('memories_token');
    setAudioUrl(null); setAudioError(false);
    fetch(`/api/memories/${m.id}/audio`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(res.status); return res.blob(); })
      .then(blob => setAudioUrl(URL.createObjectURL(blob)))
      .catch(() => setAudioError(true));
  }, [m.id]);

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return;
    if (playing) audioRef.current.pause(); else audioRef.current.play();
  };

  const markAsRead = async () => {
    try {
      await apiFetch(`/memories/${m.id}/review`, { method: 'POST' });
      setMemoryIsNew(false); onUpdate({ ...m, is_new: false });
    } catch {}
  };

  const saveTranscript = async () => {
    setSaving(true);
    try {
      await apiFetch(`/memories/${m.id}`, { method: 'PATCH', body: JSON.stringify({ transcript: transcriptDraft }) });
      setM(prev => ({ ...prev, transcript: transcriptDraft }));
      onUpdate({ ...m, transcript: transcriptDraft, is_new: memoryIsNew });
      setEditingTranscript(false);
    } catch (e) { alert('Could not save: ' + e.message); }
    setSaving(false);
  };

  const saveControls = async (newRating, newVisibility) => {
    setSavingControls(true);
    try {
      await apiFetch('/memories/' + m.id, {
        method: 'PATCH',
        body: JSON.stringify({ rating: newRating, visibility: newVisibility }),
      });
      setRating(newRating);
      setVisibility(newVisibility);
      onUpdate({ ...m, rating: newRating, visibility: newVisibility, is_new: memoryIsNew });
    } catch (e) { alert('Could not save: ' + e.message); }
    setSavingControls(false);
  };

  const reprocess = async () => {
    if (!window.confirm('Re-run AI tagging on this memory?')) return;
    setReprocessing(true);
    try {
      const updated = await apiFetch(`/memories/${m.id}/reprocess`, { method: 'POST' });
      setM(updated); onUpdate({ ...updated, is_new: memoryIsNew });
      alert('Done! Tags, title and summary updated.');
    } catch (e) { alert('Re-tag failed: ' + e.message); }
    setReprocessing(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Nav */}
      <div style={{ padding: '14px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${T.divider}` }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.inkSoft, display: 'flex', alignItems: 'center', gap: 6, fontFamily: T.body, fontSize: 15 }}>
          <Icon name="arrowLeft" size={18} stroke={1.5} color={T.inkSoft} />
          Back
        </button>
        <span style={{ fontFamily: T.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.accent }}>{m.decade}</span>
        <div style={{ width: 60 }} />
      </div>

      <div style={{ flex: 1, padding: '20px 22px 40px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Title + NEW */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <h1 style={{ fontFamily: T.display, fontSize: 28, fontWeight: 400, letterSpacing: -0.3, lineHeight: 1.15, color: T.ink, margin: 0, flex: 1 }}>{m.title}</h1>
          {memoryIsNew && <NewBadge />}
        </div>

        {/* Meta */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {m.recorded_at && <span style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute }}>{new Date(m.recorded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>}
          {m.location_name && <span style={{ fontFamily: T.body, fontSize: 13, color: T.inkMute }}>· 📍 {m.location_name}</span>}
        </div>

        {/* Rating + Visibility controls */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <select
            value={rating || ''}
            onChange={e => saveControls(e.target.value || null, visibility)}
            disabled={savingControls}
            style={{ flex: 1, background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '8px 12px', fontFamily: T.body, fontSize: 14, color: T.ink, outline: 'none', cursor: 'pointer' }}
          >
            <option value="">No Rating</option>
            <option value="R">R — Adults only</option>
            <option value="P">P — Completely private</option>
          </select>
          <select
            value={visibility}
            onChange={e => saveControls(rating, e.target.value)}
            disabled={savingControls}
            style={{ flex: 1, background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '8px 12px', fontFamily: T.body, fontSize: 14, color: T.ink, outline: 'none', cursor: 'pointer' }}
          >
            <option value="everyone">Family — everyone in my group</option>
            <option value="private">Private — only me and admin</option>
          </select>
        </div>

        {/* Tag chips */}
        {(m.people?.length > 0 || m.topics?.length > 0 || m.locations?.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {m.people?.map(p => <TagChip key={p} label={p} type="person" />)}
            {m.locations?.map(l => <TagChip key={l} label={l} type="place" />)}
            {m.topics?.map(t => <TagChip key={t} label={t} type="topic" />)}
          </div>
        )}

        {/* Audio player */}
        {audioUrl && <audio ref={audioRef} src={audioUrl} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} style={{ display: 'none' }} />}

        <button onClick={togglePlay} disabled={!audioUrl && !audioError} style={{
          display: 'flex', alignItems: 'center', gap: 14,
          background: audioError ? '#fdf0ee' : T.paperHigh,
          border: `1px solid ${audioError ? '#f0c4b8' : T.divider}`,
          borderRadius: T.radius, padding: '16px 20px',
          textAlign: 'left', width: '100%',
        }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: audioError ? '#f0c4b8' : T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: audioUrl || audioError ? 1 : 0.5 }}>
            {playing ? <Icon name="pause" size={20} color={T.paper} /> : <Icon name="play" size={20} color={T.paper} />}
          </div>
          <span style={{ fontFamily: T.display, fontSize: 18, color: audioError ? T.accent : T.ink, letterSpacing: -0.1 }}>
            {audioError ? 'Audio unavailable' : !audioUrl ? 'Loading…' : playing ? 'Pause recording' : 'Play recording'}
          </span>
        </button>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          {memoryIsNew && (
            <button onClick={markAsRead} style={{ flex: 1, background: 'none', border: `1px solid ${T.divider}`, color: T.inkSoft, fontFamily: T.body, fontSize: 14, padding: '10px', borderRadius: T.radius }}>
              ✓ Mark as read
            </button>
          )}
          <button onClick={reprocess} disabled={reprocessing} style={{ flex: 1, background: 'none', border: `1px solid ${T.divider}`, color: T.inkSoft, fontFamily: T.body, fontSize: 14, padding: '10px', borderRadius: T.radius }}>
            {reprocessing ? 'Retagging…' : 'Re-tag'}
          </button>
        </div>

        {/* Prompt — show if memory was recorded in response to a prompt */}
        {m.prompt_text && (
          <div style={{ marginBottom: 8 }}>
            <button
              onClick={() => setShowPrompt(p => !p)}
              style={{ background: 'none', border: 'none', color: T.inkMute, fontFamily: T.body, fontSize: 13, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ fontSize: 11 }}>{showPrompt ? '▾' : '▸'}</span>
              {showPrompt ? 'Hide prompt' : 'Show prompt'}
            </button>
            {showPrompt && (
              <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '14px 16px', marginTop: 8 }}>
                <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: '0 0 8px' }}>Prompt</p>
                <p style={{ fontFamily: T.display, fontSize: 16, fontStyle: 'italic', color: T.inkSoft, lineHeight: 1.65, margin: 0 }}>{m.prompt_text}</p>
              </div>
            )}
          </div>
        )}
        <MemoryPhotos memoryId={m.id} />

        {/* Transcript */}
        <div style={{ background: T.paperHigh, border: `1px solid ${T.divider}`, borderRadius: T.radius, padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <p style={{ fontFamily: T.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.inkMute, margin: 0 }}>Transcript</p>
            {!editingTranscript && (
              <button onClick={() => setEditingTranscript(true)} style={{ background: 'none', border: 'none', color: T.accent, fontFamily: T.body, fontSize: 14, fontWeight: 600, padding: 0 }}>Edit</button>
            )}
          </div>
          {editingTranscript ? (
            <>
              <textarea
                className="mem-input"
                value={transcriptDraft}
                onChange={e => setTranscriptDraft(e.target.value)}
                style={{ width: '100%', minHeight: 200, background: T.paper, border: `1.5px solid ${T.divider}`, borderRadius: T.radius, fontFamily: T.body, fontSize: 16, color: T.ink, lineHeight: 1.7, padding: '14px 16px', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={saveTranscript} disabled={saving} style={{ flex: 1, background: T.ink, border: 'none', color: T.paper, padding: '12px', borderRadius: T.radius, fontFamily: T.body, fontSize: 15, fontWeight: 600 }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => { setTranscriptDraft(m.transcript || ''); setEditingTranscript(false); }} style={{ flex: 1, background: 'none', border: `1px solid ${T.divider}`, color: T.inkSoft, padding: '12px', borderRadius: T.radius, fontFamily: T.body, fontSize: 15 }}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <p style={{ fontFamily: T.body, fontSize: 16, lineHeight: 1.75, color: m.transcript ? T.ink : T.inkMute, margin: 0, whiteSpace: 'pre-wrap' }}>
              {m.transcript || 'No transcript yet.'}
            </p>
          )}
        </div>

        {/* Delete */}
        {!confirmDelete ? (
          <button onClick={() => setConfirmDelete(true)} style={{ background: 'none', border: `1px solid ${T.divider}`, color: T.inkMute, padding: '12px', borderRadius: T.radius, fontFamily: T.body, fontSize: 14, width: '100%' }}>
            Delete this memory
          </button>
        ) : (
          <div style={{ background: '#fdf0ee', border: '1px solid #f0c4b8', borderRadius: T.radius, padding: 20, textAlign: 'center' }}>
            <p style={{ fontFamily: T.display, fontSize: 20, color: T.ink, margin: '0 0 6px', fontWeight: 400 }}>Delete this memory?</p>
            <p style={{ fontFamily: T.body, fontSize: 14, color: T.inkSoft, margin: '0 0 16px', lineHeight: 1.5 }}>This cannot be undone. The recording and transcript will be permanently deleted.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => onDelete(m.id)} style={{ flex: 1, background: T.accent, border: 'none', color: T.paper, padding: '12px', borderRadius: T.radius, fontFamily: T.body, fontSize: 15, fontWeight: 600 }}>Yes, delete it</button>
              <button onClick={() => setConfirmDelete(false)} style={{ flex: 1, background: 'none', border: `1px solid ${T.divider}`, color: T.inkSoft, padding: '12px', borderRadius: T.radius, fontFamily: T.body, fontSize: 15 }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function StorytellerHome({ user, onLogout }) {
  const [screen, setScreen] = useState('home');
  const [prompt, setPrompt] = useState('');
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [memories, setMemories] = useState([]);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [weaving, setWeaving] = useState(false);
  const [weaveResult, setWeaveResult] = useState(null);
  const [weaveRefreshKey, setWeaveRefreshKey] = useState(0);
  const [weaveSearch, setWeaveSearch] = useState('');
  const [writingText, setWritingText] = useState('');
  const [writingPrompt, setWritingPrompt] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const promptRef = useRef('');
  const pendingPhotoRef = useRef(null);
  const suggestedPromptIdRef = useRef(null);
  const connectionQuality = useConnectionQuality();

  useEffect(() => {
    document.body.style.background = T.bg;
    document.body.style.color = T.ink;
    return () => { document.body.style.background = ''; document.body.style.color = ''; };
  }, []);

  const getPrompt = async () => {
    setLoadingPrompt(true);
    try {
      const data = await apiFetch('/memories/generate-prompt', { method: 'POST', body: JSON.stringify({}) });
      setPrompt(data.prompt); promptRef.current = data.prompt;
      suggestedPromptIdRef.current = data.suggested_prompt_id || null;
      setScreen('prompted');
    } catch { setErrorMsg('Could not load a prompt. Try again.'); setScreen('error'); }
    setLoadingPrompt(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(200); mediaRecorderRef.current = mr; setSeconds(0); setScreen('recording');
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch { setErrorMsg('We can\'t hear you just yet. Please allow microphone access and try again.'); setScreen('error'); }
  };

  const stopAndSubmit = () => {
    clearInterval(timerRef.current);
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    mr.onstop = async () => {
      setScreen('processing');
      try {
        let lat = null, lng = null;
        try {
          const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 }));
          lat = pos.coords.latitude; lng = pos.coords.longitude;
        } catch {}
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const fd = new FormData();
        fd.append('audio', blob, 'memory.webm');
        fd.append('recorded_at', new Date().toISOString());
        if (lat) fd.append('latitude', lat);
        if (lng) fd.append('longitude', lng);
        if (promptRef.current) fd.append('prompt_text', promptRef.current);
        if (suggestedPromptIdRef.current) fd.append('suggested_prompt_id', suggestedPromptIdRef.current);
        const token = localStorage.getItem('memories_token');
        const res = await fetch(`${API}/memories`, { method: 'POST', body: fd, headers: { 'Authorization': `Bearer ${token}` } });
        const saved = await res.json();

        // Attach the photo picked before recording, if any
        if (pendingPhotoRef.current && saved?.id) {
          try {
            const pfd = new FormData();
            pfd.append('photo', pendingPhotoRef.current);
            pfd.append('memory_id', saved.id);
            await fetch('/api/photos/upload', { method: 'POST', body: pfd, headers: { 'Authorization': `Bearer ${token}` } });
          } catch (e) { console.error('Photo attach failed:', e); }
        }
        pendingPhotoRef.current = null;

        promptRef.current = '';
        suggestedPromptIdRef.current = null;
        setScreen('done');
      } catch { setErrorMsg('Something went wrong saving your memory. Please try again.'); setScreen('error'); }
      mr.stream.getTracks().forEach(t => t.stop());
    };
    mr.stop();
  };

  const submitWrittenMemory = async () => {
    if (!writingText.trim()) return;
    setScreen('processing');
    try {
      await apiFetch('/memories/text', {
        method: 'POST',
        body: JSON.stringify({ text: writingText.trim(), recorded_at: new Date().toISOString(), prompt_text: writingPrompt || null, suggested_prompt_id: suggestedPromptIdRef.current }),
      });
      setWritingText(''); setWritingPrompt('');
      suggestedPromptIdRef.current = null;
      setScreen('done');
    } catch { setErrorMsg('Something went wrong saving your memory. Please try again.'); setScreen('error'); }
  };

  const loadMemories = async () => {
    try {
      const data = await apiFetch(`/memories?user_id=${user.id}`);
      setMemories(data); setScreen('archive');
    } catch (e) { setErrorMsg(e.message); setScreen('error'); }
  };

  const runWeave = async () => {
    setWeaving(true); setWeaveResult(null);
    try {
      const body = { user_id: user.id };
      if (weaveSearch.trim()) body.query = weaveSearch.trim();
      const res = await apiFetch('/memories/aggregate', { method: 'POST', body: JSON.stringify(body) });
      setWeaveResult(res.narrative);
      setWeaveRefreshKey(k => k + 1);
    } catch (e) { alert('Weave failed: ' + e.message); }
    setWeaving(false);
  };

  const handleUpdateMemory = (updated) => {
    setMemories(prev => prev.map(m => m.id === updated.id ? updated : m));
    setSelectedMemory(updated);
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/memories/${id}`, { method: 'DELETE' });
      setMemories(prev => prev.filter(m => m.id !== id));
      setSelectedMemory(null); setScreen('archive');
    } catch (e) { alert('Could not delete: ' + e.message); }
  };

  const reset = () => { setScreen('home'); setPrompt(''); promptRef.current = ''; suggestedPromptIdRef.current = null; pendingPhotoRef.current = null; setSeconds(0); setErrorMsg(''); };

  // Archive + detail routing
  if (screen === 'archive' && selectedMemory) {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <MemoryDetail memory={selectedMemory} onBack={() => setSelectedMemory(null)} onDelete={handleDelete} onUpdate={handleUpdateMemory} />
      </>
    );
  }

  if (screen === 'archive') {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <ArchiveScreen
          memories={memories} onSelect={setSelectedMemory} onBack={reset}
          onWeave={runWeave} weaving={weaving} weaveResult={weaveResult}
          setWeaveResult={setWeaveResult} weaveSearch={weaveSearch} setWeaveSearch={setWeaveSearch} userId={user.id} weaveRefreshKey={weaveRefreshKey}
        />
      </>
    );
  }

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      {screen === 'home' && (
        <HomeScreen
          user={user}
          onRecord={startRecording}
          onWrite={() => { setWritingPrompt(''); setScreen('write'); }}
          onPrompt={getPrompt}
          onArchive={loadMemories}
          onLogout={onLogout}
          loadingPrompt={loadingPrompt}
          connectionQuality={connectionQuality}
        />
      )}
      {screen === 'prompted' && (
        <PromptedScreen
          prompt={prompt}
          onRecord={startRecording}
          onWrite={(p) => { setWritingPrompt(p); setScreen('write'); }}
          onBack={reset}
        />
      )}
      {screen === 'recording' && (
        <RecordingScreen prompt={prompt} seconds={seconds} onStop={stopAndSubmit} onCancel={() => { clearInterval(timerRef.current); mediaRecorderRef.current?.stop(); reset(); }} />
      )}
      {screen === 'write' && (
        <WriteScreen writingPrompt={writingPrompt} writingText={writingText} setWritingText={setWritingText} onSubmit={submitWrittenMemory} onBack={reset} />
      )}
      {screen === 'processing' && <ProcessingScreen />}
      {screen === 'done' && <SavedScreen onRecordAnother={reset} onViewStories={loadMemories} />}
      {screen === 'error' && <ErrorScreen msg={errorMsg} onBack={reset} />}
    </>
  );
}
